import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Eye, FileSignature, Search, Settings2, UserPlus, Undo2, X } from 'lucide-react';
import {
  BoutonMint,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { CLASSE_ACTION_LIGNE, PiecesFormateur } from '../components/formateurs/PiecesFormateur';
import { FacturesFormateur } from '../components/formateurs/FacturesFormateur';
import { CartePrevisionFormateur } from '../components/formateurs/PrevisionHeures';
import { CarteDeclarationFormateur } from '../components/formateurs/HeuresRealisees';
import {
  CLASSE_CHAMP_FORMATEUR,
  ChampFormateur,
  ModaleConvention,
  ModaleMotif,
} from '../components/formateurs/ModalesFormateur';
import { usePerimetre } from '../site/usePerimetre';
import { useReferentiels } from '../lib/useDocuments';
import {
  LIBELLES_STATUT_FORMATEUR,
  dateReunion,
  euros,
  heures,
  jourCourt,
  messageRefus,
  urlConventionFormateur,
  urlFactureFormateur,
  urlPieceFormateur,
  useCreerFicheFormateur,
  useFicheFormateur,
  useFormateurs,
  useGenererConventionPole,
  useReglerFormateur,
  useVerifierFacture,
  useVerifierPieceFormateur,
  type FicheFormateur,
  type StatutFormateur,
} from '../lib/useFormateurs';

function initiales(prenom: string, nom: string): string {
  return `${prenom.charAt(0)}${nom.charAt(0)}`.toLocaleUpperCase('fr');
}

interface Consultation {
  libelle: string;
  url: string;
  nomFichier: string;
  typeMime: string;
}

type Motif =
  | { nature: 'PIECE'; id: string; libelle: string }
  | { nature: 'FACTURE'; id: string; libelle: string };

/**
 * Écran « Formateurs » (CDC §7.4) : l'assistant(e) pédagogique ou
 * administratif y vérifie ce que les formateurs de son antenne ont transmis —
 * pièces obligatoires, SIRET et IBAN recopiés sur la facture, montant déclaré
 * face aux heures validées. Il y fixe aussi statut et taux horaire, et génère
 * la convention. Le pôle facturation y lit sans agir.
 */
export function FormateursPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useFormateurs(site);
  // La relance d'une déclaration d'heures (TF-420) mène à la fiche : `?formateur=<id>`.
  const [parametres] = useSearchParams();
  const [selection, setSelection] = useState<string | null>(() => parametres.get('formateur'));
  const [recherche, setRecherche] = useState('');
  const [aVerifierSeulement, setAVerifierSeulement] = useState(false);
  const [compteACreer, setCompteACreer] = useState<{ utilisateurId: string; nom: string } | null>(null);

  const formateurs = useMemo(() => {
    const terme = recherche.trim().toLocaleLowerCase('fr');
    return (data?.formateurs ?? []).filter(
      (ligne) =>
        (!terme || `${ligne.prenom} ${ligne.nom}`.toLocaleLowerCase('fr').includes(terme)) &&
        (!aVerifierSeulement || ligne.piecesAVerifier + ligne.facturesAVerifier > 0),
    );
  }, [data, recherche, aVerifierSeulement]);

  if (isLoading) return <Chargement />;
  if (error || !data) return <MessageErreur message={messageRefus(error, 'Liste indisponible.')} />;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Gestion des formateurs"
        sousTitre={
          data.peutVerifier
            ? 'Pièces obligatoires, coordonnées et factures transmises par les formateurs, à vérifier'
            : 'Pièces, conventions et factures des formateurs · consultation'
        }
        actions={<BarreSites />}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Compteur libelle="Formateurs" valeur={data.compteurs.formateurs} />
        <Compteur
          libelle="Pièces à vérifier"
          valeur={data.compteurs.piecesAVerifier}
          ton={data.compteurs.piecesAVerifier > 0 ? 'warm' : 'navy'}
        />
        <Compteur
          libelle="Factures à vérifier"
          valeur={data.compteurs.facturesAVerifier}
          ton={data.compteurs.facturesAVerifier > 0 ? 'warm' : 'navy'}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Liste — masquée sous la largeur tablette quand une fiche est ouverte. */}
        <div className={`min-w-0 space-y-6 ${selection ? 'hidden lg:block' : ''}`}>
          <Carte titre="Équipe pédagogique" actions={<span className="text-xs text-codialis-gray">{formateurs.length}</span>} sansBordure>
            <div className="space-y-2 border-b border-gray-100 px-4 py-3 sm:px-5">
              <label className="relative block">
                <span className="sr-only">Rechercher un formateur</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray" aria-hidden="true" />
                <input
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  placeholder="Rechercher…"
                  className={`${CLASSE_CHAMP_FORMATEUR} pl-9`}
                />
              </label>
              <label className="flex min-h-[40px] items-center gap-2 text-xs text-codialis-navy">
                <input
                  type="checkbox"
                  checked={aVerifierSeulement}
                  onChange={(e) => setAVerifierSeulement(e.target.checked)}
                  className="h-5 w-5 shrink-0"
                />
                Seulement ceux qui ont quelque chose à vérifier
              </label>
            </div>
            {formateurs.length === 0 ? (
              <p className="px-4 py-5 text-sm text-codialis-gray sm:px-5">Aucun formateur à afficher.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {formateurs.map((ligne) => {
                  const actif = ligne.id === selection;
                  const aVerifier = ligne.piecesAVerifier + ligne.facturesAVerifier;
                  return (
                    <li key={ligne.id}>
                      <button
                        type="button"
                        onClick={() => setSelection(ligne.id)}
                        aria-current={actif ? 'true' : undefined}
                        className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-gray-50 sm:px-5 ${
                          actif ? 'border-l-2 border-codialis-mint bg-codialis-mint/5' : ''
                        }`}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
                          {initiales(ligne.prenom, ligne.nom)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-codialis-navy">
                            {ligne.prenom} {ligne.nom}
                          </span>
                          <span className="block truncate text-xs text-codialis-gray">
                            {ligne.statutLibelle}
                            {ligne.piecesNonConformes > 0 &&
                              ` · ${ligne.piecesNonConformes} pièce${ligne.piecesNonConformes > 1 ? 's' : ''} non conforme${ligne.piecesNonConformes > 1 ? 's' : ''}`}
                          </span>
                        </span>
                        {aVerifier > 0 && <Pastille texte={`${aVerifier} à vérifier`} ton="warning" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Carte>

          {data.sansFiche.length > 0 && (
            <Carte titre="Comptes formateur sans fiche" sansBordure>
              <p className="px-4 pt-3 text-xs text-codialis-gray sm:px-5">
                Sans fiche, le formateur ne peut rien déposer ni facturer.
              </p>
              <ul className="divide-y divide-gray-100">
                {data.sansFiche.map((compte) => (
                  <li key={compte.utilisateurId} className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-5">
                    <div className="min-w-0 flex-1 basis-40">
                      <div className="truncate text-sm font-medium text-codialis-navy">
                        {compte.prenom} {compte.nom}
                      </div>
                      <div className="truncate text-[11px] text-codialis-gray">{compte.email}</div>
                    </div>
                    {data.peutVerifier && (
                      <button
                        type="button"
                        onClick={() =>
                          setCompteACreer({ utilisateurId: compte.utilisateurId, nom: `${compte.prenom} ${compte.nom}` })
                        }
                        className={`${CLASSE_ACTION_LIGNE} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
                      >
                        <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                        Créer la fiche
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </Carte>
          )}
        </div>

        <div className={`min-w-0 lg:col-span-2 ${selection ? '' : 'hidden lg:block'}`}>
          {selection ? (
            <DetailFormateur formateurId={selection} onRetour={() => setSelection(null)} />
          ) : (
            <div className="rounded-2xl bg-white p-6 text-sm text-codialis-gray shadow-card">
              Choisissez un formateur pour vérifier ses pièces et ses factures.
            </div>
          )}
        </div>
      </div>

      {compteACreer && (
        <ModaleCreationFiche
          compte={compteACreer}
          onFermer={() => setCompteACreer(null)}
          onCree={(formateurId) => {
            setCompteACreer(null);
            setSelection(formateurId);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Détail d'un formateur
// ---------------------------------------------------------------------------

function DetailFormateur({ formateurId, onRetour }: { formateurId: string; onRetour: () => void }): JSX.Element {
  const { data, isLoading, error } = useFicheFormateur(formateurId);

  if (isLoading) return <Chargement />;
  if (error || !data) return <MessageErreur message={messageRefus(error, 'Fiche indisponible.')} />;
  return <FicheDetaillee fiche={data.fiche} onRetour={onRetour} />;
}

function FicheDetaillee({ fiche, onRetour }: { fiche: FicheFormateur; onRetour: () => void }): JSX.Element {
  const toast = useToast();
  const verifierPiece = useVerifierPieceFormateur();
  const verifierFacture = useVerifierFacture();
  const genererConvention = useGenererConventionPole();
  const { data: referentiels } = useReferentiels();

  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [motif, setMotif] = useState<Motif | null>(null);
  const [reglages, setReglages] = useState(false);
  const [convention, setConvention] = useState(false);
  const { coordonnees } = fiche;
  const independant = fiche.statut === 'INDEPENDANT';

  const conforme = (documentId: string): void => {
    verifierPiece.mutate(
      { documentId, decision: 'CONFORME' },
      {
        onSuccess: () => toast('Pièce conforme'),
        onError: (erreur) => toast(messageRefus(erreur, 'Vérification impossible.')),
      },
    );
  };

  const valider = (factureId: string): void => {
    verifierFacture.mutate(
      { factureId, decision: 'VERIFIEE' },
      {
        onSuccess: () => toast('Facture vérifiée'),
        onError: (erreur) => toast(messageRefus(erreur, 'Vérification impossible.')),
      },
    );
  };

  const statistiques: [string, string][] = [
    ['Heures animées', heures(fiche.activite.heuresAnimees)],
    ['Fiches validées', String(fiche.activite.fichesValidees)],
    ['Taux horaire', fiche.tauxHoraire !== null ? `${euros(fiche.tauxHoraire)} HT` : independant ? 'Non fixé' : 'Salarié'],
    ['Pièces non conformes', String(fiche.piecesNonConformes.length)],
  ];

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onRetour}
        className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-codialis-sky lg:hidden"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Retour à la liste
      </button>

      <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
        <div className="mb-6 flex flex-wrap items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-codialis-sky to-codialis-blue text-lg font-bold text-white sm:h-16 sm:w-16 sm:text-xl">
            {initiales(fiche.prenom, fiche.nom)}
          </div>
          <div className="min-w-0 flex-1 basis-48">
            <h2 className="font-display text-xl font-bold text-codialis-navy sm:text-2xl">
              {fiche.prenom} {fiche.nom}
            </h2>
            <p className="text-sm text-codialis-gray">
              {fiche.statutLibelle}
              {!fiche.actif && ' · compte désactivé'}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-codialis-gray">
              {fiche.sites.map((site) => (
                <PastilleSite key={site.code} site={site.code} />
              ))}
              <span className="break-all">{fiche.email}</span>
              {fiche.telephone && <span>· {fiche.telephone}</span>}
            </p>
          </div>
          {fiche.peutVerifier && (
            <div className="flex flex-wrap gap-2">
              <BoutonSecondaire onClick={() => setReglages(true)}>
                <Settings2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Statut et taux
              </BoutonSecondaire>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {statistiques.map(([libelle, valeur]) => (
            <div key={libelle} className="rounded-xl bg-gray-50 p-3 text-center">
              <div className="font-display text-lg font-extrabold text-codialis-navy sm:text-xl">{valeur}</div>
              <div className="text-[11px] text-codialis-gray">{libelle}</div>
            </div>
          ))}
        </div>
      </div>

      <Carte titre="Pièces obligatoires" sansBordure>
        <PiecesFormateur
          pieces={fiche.pieces}
          actionsDepot={(depot, piece) => (
            <>
              <button
                type="button"
                onClick={() =>
                  setConsultation({
                    libelle: `${piece.libelle} · ${fiche.prenom} ${fiche.nom}`,
                    url: urlPieceFormateur(depot.id),
                    nomFichier: depot.nomFichier,
                    typeMime: depot.typeMime,
                  })
                }
                className={`${CLASSE_ACTION_LIGNE} text-codialis-sky hover:bg-white`}
              >
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                Voir
              </button>
              {depot.refusVerification === null && (
                <>
                  <button
                    type="button"
                    onClick={() => conforme(depot.id)}
                    disabled={verifierPiece.isPending}
                    className={`${CLASSE_ACTION_LIGNE} bg-codialis-mint/30 text-codialis-navy hover:bg-codialis-mint/50`}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    Conforme
                  </button>
                  <button
                    type="button"
                    onClick={() => setMotif({ nature: 'PIECE', id: depot.id, libelle: piece.libelle })}
                    className={`${CLASSE_ACTION_LIGNE} text-red-700 hover:bg-white`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    Refuser
                  </button>
                </>
              )}
            </>
          )}
        />
      </Carte>

      {independant && (
        <Carte titre="Coordonnées de facturation" sansBordure>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 p-4 text-sm sm:grid-cols-2 sm:p-6">
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">SIRET</dt>
              <dd className="flex flex-wrap items-center gap-2 font-mono text-codialis-navy">
                {coordonnees.siret ?? 'Non renseigné'}
                {coordonnees.siret && (
                  <Pastille
                    texte={coordonnees.siretValide ? 'Clé valide' : 'Clé invalide'}
                    ton={coordonnees.siretValide ? 'success' : 'danger'}
                  />
                )}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Raison sociale</dt>
              <dd className="text-codialis-navy">{coordonnees.raisonSociale ?? '—'}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">IBAN</dt>
              <dd className="break-all font-mono text-codialis-navy">{coordonnees.iban ?? 'Non renseigné'}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">BIC · titulaire</dt>
              <dd className="text-codialis-navy">
                <span className="font-mono">{coordonnees.bic ?? '—'}</span>
                {coordonnees.titulaireCompte ? ` · ${coordonnees.titulaireCompte}` : ''}
              </dd>
            </div>
            <div className="min-w-0 sm:col-span-2">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Adresse</dt>
              <dd className="text-codialis-navy">
                {[coordonnees.adresse, [coordonnees.codePostal, coordonnees.ville].filter(Boolean).join(' ')]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </dd>
            </div>
            {coordonnees.modifieesLe && (
              <p className="text-[11px] text-codialis-gray sm:col-span-2">
                Saisies par le formateur, mises à jour le {dateReunion(coordonnees.modifieesLe)}. Chaque facture garde
                celles de sa transmission.
              </p>
            )}
          </dl>
        </Carte>
      )}

      <CartePrevisionFormateur formateurId={fiche.id} />
      <CarteDeclarationFormateur formateurId={fiche.id} />

      {independant && (
        <Carte titre="Factures" sansBordure>
          <FacturesFormateur
            factures={fiche.factures}
            coordonnees
            siVide="Aucune facture transmise par ce formateur."
            actions={(facture) => (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setConsultation({
                      libelle: `Facture n° ${facture.numero} · ${fiche.prenom} ${fiche.nom}`,
                      url: urlFactureFormateur(facture.id),
                      nomFichier: facture.nomFichier,
                      typeMime: 'application/pdf',
                    })
                  }
                  className={`${CLASSE_ACTION_LIGNE} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                  Voir la facture
                </button>
                {fiche.peutVerifier && facture.statut === 'TRANSMISE' && (
                  <>
                    <button
                      type="button"
                      onClick={() => valider(facture.id)}
                      disabled={facture.refusVerification !== null || verifierFacture.isPending}
                      className={`${CLASSE_ACTION_LIGNE} bg-codialis-mint/30 text-codialis-navy hover:bg-codialis-mint/50`}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      Vérifiée
                    </button>
                    {facture.refusRenvoi === null && (
                      <button
                        type="button"
                        onClick={() => setMotif({ nature: 'FACTURE', id: facture.id, libelle: facture.numero })}
                        className={`${CLASSE_ACTION_LIGNE} text-red-700 hover:bg-gray-50`}
                      >
                        <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                        Renvoyer au formateur
                      </button>
                    )}
                    {facture.refusVerification !== null && (
                      <span className="basis-full text-[11px] text-amber-800">{facture.refusVerification}</span>
                    )}
                  </>
                )}
              </>
            )}
          />
        </Carte>
      )}

      {independant && (
        <Carte
          titre="Conventions de formation"
          actions={
            fiche.peutVerifier && fiche.refusConvention === null ? (
              <BoutonMint onClick={() => setConvention(true)}>
                <FileSignature className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Générer
              </BoutonMint>
            ) : undefined
          }
          sansBordure
        >
          {fiche.conventions.length === 0 ? (
            <p className="px-4 py-5 text-sm text-codialis-gray sm:px-6">Aucune convention générée.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {fiche.conventions.map((ligne, rang) => (
                <li key={ligne.id} className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:px-6">
                  <div className="min-w-0 flex-1 basis-56">
                    <div className="text-sm font-medium text-codialis-navy">
                      Du {jourCourt(ligne.debut)} au {jourCourt(ligne.fin)}
                    </div>
                    <div className="text-[11px] text-codialis-gray">
                      Générée le {dateReunion(ligne.genereLe)}
                      {ligne.generePar ? ` par ${ligne.generePar}` : ''}
                      {ligne.heuresPrevues !== null && ` · ${heures(ligne.heuresPrevues)} prévues`}
                      {ligne.tauxHoraire !== null && ` · ${euros(ligne.tauxHoraire)} HT de l’heure`}
                      {ligne.formations.length > 0 && ` · ${ligne.formations.join(', ')}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setConsultation({
                        libelle: `Convention · ${fiche.prenom} ${fiche.nom}`,
                        url: urlConventionFormateur(ligne.id),
                        nomFichier: ligne.nomFichier,
                        typeMime: 'application/pdf',
                      })
                    }
                    className={`${CLASSE_ACTION_LIGNE} text-codialis-sky hover:bg-gray-50`}
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                    Voir
                  </button>
                  {ligne.exemplaire ? (
                    <Pastille texte={`Signée · ${ligne.exemplaire.statutLibelle.toLowerCase()}`} ton={ligne.exemplaire.statutTon} />
                  ) : (
                    <Pastille texte={rang === 0 ? 'À signer' : 'Non signée'} ton={rang === 0 ? 'warning' : 'neutral'} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </Carte>
      )}

      {motif && (
        <ModaleMotif
          titre={motif.nature === 'PIECE' ? `Refuser · ${motif.libelle}` : `Renvoyer la facture n° ${motif.libelle}`}
          sousTitre="Le formateur est prévenu, motif compris, par mail et dans son espace."
          libelle={motif.nature === 'PIECE' ? 'Refuser la pièce' : 'Renvoyer'}
          enCours={verifierPiece.isPending || verifierFacture.isPending}
          onFermer={() => setMotif(null)}
          onValider={async (texte) => {
            if (motif.nature === 'PIECE') {
              await verifierPiece.mutateAsync({ documentId: motif.id, decision: 'REFUSE', motif: texte });
              toast('Pièce refusée · formateur prévenu');
            } else {
              await verifierFacture.mutateAsync({ factureId: motif.id, decision: 'A_CORRIGER', motif: texte });
              toast('Facture renvoyée · formateur prévenu');
            }
            setMotif(null);
          }}
        />
      )}

      {reglages && <ModaleReglages fiche={fiche} onFermer={() => setReglages(false)} />}

      {convention && (
        <ModaleConvention
          formations={(referentiels?.formations ?? []).map((formation) => ({
            code: formation.code,
            titre: formation.titre,
          }))}
          enCours={genererConvention.isPending}
          onFermer={() => setConvention(false)}
          onGenerer={async (saisie) => {
            await genererConvention.mutateAsync({ formateurId: fiche.id, ...saisie });
            setConvention(false);
            toast('Convention générée · formateur prévenu');
          }}
        />
      )}

      {consultation && (
        <VisionneusePiece
          ouverte
          libellePiece={consultation.libelle}
          fichiers={[
            {
              id: 'fichier',
              nomFichier: consultation.nomFichier,
              typeMime: consultation.typeMime,
              tailleOctets: 0,
              deposeLe: '',
            },
          ]}
          urlFichier={() => consultation.url}
          onFermer={() => setConsultation(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fenêtres du pôle
// ---------------------------------------------------------------------------

function ModaleReglages({ fiche, onFermer }: { fiche: FicheFormateur; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const regler = useReglerFormateur();
  const [statut, setStatut] = useState<StatutFormateur>(fiche.statut);
  const [taux, setTaux] = useState(fiche.tauxHoraire !== null ? String(fiche.tauxHoraire).replace('.', ',') : '');
  const [refus, setRefus] = useState<string | null>(null);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setRefus(null);
    regler.mutate(
      { formateurId: fiche.id, statut, tauxHoraire: taux.replace(',', '.') },
      {
        onSuccess: () => {
          toast('Réglages enregistrés');
          onFermer();
        },
        onError: (erreur) => setRefus(messageRefus(erreur, 'Enregistrement impossible.')),
      },
    );
  };

  return (
    <Modale
      ouverte
      titre={`Statut et taux · ${fiche.prenom} ${fiche.nom}`}
      sousTitre="Le statut décide des pièces demandées ; le taux est porté sur la convention et sert de repère à la facture."
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <ChampFormateur libelle="Statut">
          <select value={statut} onChange={(e) => setStatut(e.target.value as StatutFormateur)} className={CLASSE_CHAMP_FORMATEUR}>
            {(Object.keys(LIBELLES_STATUT_FORMATEUR) as StatutFormateur[]).map((valeur) => (
              <option key={valeur} value={valeur}>
                {LIBELLES_STATUT_FORMATEUR[valeur]}
              </option>
            ))}
          </select>
        </ChampFormateur>
        <ChampFormateur libelle="Taux horaire (€ HT)" aide="Vide : non fixé. La convention laisse alors la case à compléter.">
          <input value={taux} onChange={(e) => setTaux(e.target.value)} inputMode="decimal" className={`${CLASSE_CHAMP_FORMATEUR} font-mono`} />
        </ChampFormateur>
        {refus && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">{refus}</p>}
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonMint type="submit" disabled={regler.isPending}>
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonMint>
        </div>
      </form>
    </Modale>
  );
}

function ModaleCreationFiche({
  compte,
  onFermer,
  onCree,
}: {
  compte: { utilisateurId: string; nom: string };
  onFermer: () => void;
  onCree: (formateurId: string) => void;
}): JSX.Element {
  const toast = useToast();
  const creer = useCreerFicheFormateur();
  const [statut, setStatut] = useState<StatutFormateur>('INDEPENDANT');
  const [refus, setRefus] = useState<string | null>(null);

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setRefus(null);
    creer.mutate(
      { utilisateurId: compte.utilisateurId, statut },
      {
        onSuccess: ({ fiche }) => {
          toast('Fiche formateur créée');
          onCree(fiche.id);
        },
        onError: (erreur) => setRefus(messageRefus(erreur, 'Création impossible.')),
      },
    );
  };

  return (
    <Modale ouverte titre={`Créer la fiche · ${compte.nom}`} onFermer={onFermer} largeur="max-w-md">
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <ChampFormateur libelle="Statut" aide="Un indépendant facture et signe une convention ; un salarié est payé par la paie.">
          <select value={statut} onChange={(e) => setStatut(e.target.value as StatutFormateur)} className={CLASSE_CHAMP_FORMATEUR}>
            {(Object.keys(LIBELLES_STATUT_FORMATEUR) as StatutFormateur[]).map((valeur) => (
              <option key={valeur} value={valeur}>
                {LIBELLES_STATUT_FORMATEUR[valeur]}
              </option>
            ))}
          </select>
        </ChampFormateur>
        {refus && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">{refus}</p>}
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonMint type="submit" disabled={creer.isPending}>
            {creer.isPending ? 'Création…' : 'Créer la fiche'}
          </BoutonMint>
        </div>
      </form>
    </Modale>
  );
}
